/**
 * FlexibleLayout - composable screen assembled from draggable, ordered sections
 *
 * Sections render in ascending section.order.
 * Layout modes:
 * - scrollable=true: sections stack in a scroll container (overflow-y auto)
 * - scrollable=false: sections fill screen height using flexGrow/flexShrink
 *
 * Supports the current FlexibleLayout section catalog: Text, Media, ProductCatalog,
 * Button/PurchaseButton, selection lists, BulletList, CustomEmbed, PaywallHeader,
 * TextField, EmptySpace, Header, Footer, and Background.
 * Navigation: full-control header + actionBar at screen level + section-level button triggers
 */

import React, { memo, useContext, useMemo, useRef } from 'react';
import { useStickyScrollClearance } from '@modules/screen-view-v2/hooks/useStickyScrollClearance';
import { SemanticMarkupContext } from '@modules/screen-view-v2/context/SemanticMarkupContext';
import { buildArticleOutline, resolveArticleSemanticTags } from '@modules/screen-view-v2/utils/semanticMarkup';
import { findLeadFigureSectionId } from '@modules/screen-view-v2/utils/articleLeadFigure';
import { ArticleRenderContext } from '@modules/screen-view-v2/context/ArticleRenderContext';
import { hasOwn } from '@modules/screen-view-v2/utils/articlePresentationCss';
import { collapseArticleSpacing } from '@modules/screen-view-v2/utils/articleSpacingCollapse';
import type { StepNode, OptionsItem, ProductOffer } from '@segmently/schema-models/v2';
import type { OnActionCallback, OnEventCallback } from '../../../types/events';
import type { TemplateVariables } from '../../../utils/contentResolver';
import type { PreviewMode } from '../../../types/previewMode';
import type { ButtonVisibilityState } from '../../../utils/buttonVisibility';
import type {
  CustomScreenButtonActionRequest,
  CustomScreenOptionActionRequest,
  LegacyInitData,
} from '../../../types/customScreenBridge.types';
import type { LegalLinksEventContext } from '../../shared/LegalLinks';

import { Background } from '../../shared/Background';
import { HeaderBar } from '../../shared/HeaderBar';
import { ActionBar } from '../../shared/ActionBar';
import { SectionWrapper } from './SectionWrapper';
import { SectionRenderer } from './SectionRenderer';
import type { SectionRendererProps } from './SectionRenderer';
import { StickyContainer } from './StickyContainer';
import type { StickyContainerConfig } from '../../../types/stickyContainer';
import { mapEdgeInsetsToCSS } from '../../../utils/styleMapper';
import { mergeSectionLayout } from '../../../utils/sectionLayoutMerge';
import { buildLinkedProductRenderResult } from './linkedProductFallbacks';
import styles from './FlexibleLayout.module.css';

export interface FlexibleLayoutProps {
  step: StepNode;
  ssrMode?: boolean;
  locale: string;
  variables?: TemplateVariables;
  resolvedScreenValues?: Record<string, string>;
  onAction?: OnActionCallback;
  onEvent?: OnEventCallback;
  editMode?: boolean;
  previewMode?: PreviewMode;
  /** Per-product template variables for ProductCatalog sections */
  itemVariablesMap?: Record<string, TemplateVariables>;
  /** Per-section selected product index for ProductCatalog sections (sectionId → index) */
  selectedProductIndexMap?: Map<string, number>;
  /** Callback scoped to section when product is selected */
  onSectionProductSelect?: (sectionId: string, item: ProductOffer, index: number) => void;
  /** Per-section selected option indices for OptionsList sections (sectionId → Set<index>) */
  selectedOptionIndicesMap?: Map<string, Set<number>>;
  /** Callback when an option is selected/deselected within a section */
  onSectionOptionSelect?: (sectionId: string, item: OptionsItem, index: number, selected: boolean) => void;
  /** Per-section button visibility state for Button sections (sectionId → { disabled, hidden }) */
  sectionButtonStates?: Record<string, ButtonVisibilityState>;
  /** Raw HTML per section (section.id → html) for CustomEmbed sections */
  embedHtmlMap?: Record<string, string>;
  /** Init data per section (section.id → LegacyInitData) for CustomEmbed sections */
  embedInitDataMap?: Record<string, LegacyInitData>;
  /** Callback when a CustomEmbed section requests forward navigation */
  onSectionEmbedNavigate?: (sectionId: string, data: Record<string, unknown>) => void;
  /** Callback when a CustomEmbed section requests back navigation */
  onSectionEmbedBack?: () => void;
  /** Callback for analytics events from CustomEmbed sections */
  onSectionEmbedTrackEvent?: (name: string, params?: Record<string, unknown>) => void;
  /** Callback when a CustomEmbed section calls sdk.setValue() */
  onSectionEmbedValueChange?: (key: string, value: unknown) => void;
  /** Callback when a CustomEmbed section calls sdk.setVariable() */
  onSectionEmbedSetVariable?: (variableId: string, value: unknown) => void;
  /** Callback when a CustomEmbed section triggers an option action on a child section */
  onSectionEmbedTriggerOptionAction?: (embedSectionId: string, request: CustomScreenOptionActionRequest) => void;
  /** Callback when a CustomEmbed section triggers a Button child section action */
  onSectionEmbedTriggerButtonAction?: (embedSectionId: string, request: CustomScreenButtonActionRequest) => void;
  /** Callback when a CustomEmbed section calls sdk.selectProduct() */
  onSectionEmbedSelectProduct?: (embedSectionId: string, childSectionId: string, productIndex: number, productId: string) => void;
  /** Callback when a CustomEmbed section calls sdk.purchaseProduct() */
  onSectionEmbedPurchaseProduct?: (embedSectionId: string, requestId: string, productId: string, childSectionId?: string) => void;
  /** Purchase result to push back to CustomEmbed iframe */
  sectionEmbedPurchaseResult?: { requestId: string; status: 'success' | 'cancel' | 'error'; error?: string; _ts: number } | null;
  /** Variable update broadcast for cross-section reactivity */
  lastVariableUpdate?: { variableId: string; value: unknown; sourceIframeId?: string; _ts: number } | null;
  /** Per-section text field values (sectionId → value) for TextField sections */
  sectionTextFieldValues?: Map<string, string>;
  /** Per-section text field errors (sectionId → error) for TextField sections */
  sectionTextFieldErrors?: Map<string, string | null>;
  /** Callback when text field value changes within a section */
  onSectionTextFieldChange?: (sectionId: string, value: string) => void;
  /** Callback when text field blurs within a section (validation) */
  onSectionTextFieldBlur?: (sectionId: string) => void;
  /** Payment loading state */
  isLoading?: boolean;
  /** Legal links callback */
  onLinkClick?: (context: LegalLinksEventContext) => void;
}

export const FlexibleLayout = memo(function FlexibleLayout({
  step,
  ssrMode,
  locale,
  variables,
  resolvedScreenValues,
  onAction,
  onEvent,
  editMode = false,
  previewMode,
  itemVariablesMap,
  selectedProductIndexMap,
  onSectionProductSelect,
  selectedOptionIndicesMap,
  onSectionOptionSelect,
  sectionButtonStates,
  embedHtmlMap,
  embedInitDataMap,
  onSectionEmbedNavigate,
  onSectionEmbedBack,
  onSectionEmbedTrackEvent,
  onSectionEmbedValueChange,
  onSectionEmbedSetVariable,
  onSectionEmbedTriggerOptionAction,
  onSectionEmbedTriggerButtonAction,
  onSectionEmbedSelectProduct,
  onSectionEmbedPurchaseProduct,
  sectionEmbedPurchaseResult,
  lastVariableUpdate,
  sectionTextFieldValues,
  sectionTextFieldErrors,
  onSectionTextFieldChange,
  onSectionTextFieldBlur,
  isLoading = false,
  onLinkClick: _onLinkClick,
}: FlexibleLayoutProps): React.ReactElement {
  const { content } = step;
  const config = content?.flexibleLayout;
  const sections = config?.sections ?? [];
  const scrollable = config?.scrollable ?? true;
  // Screen-level section defaults (v2.19.0): applied UNDER each section's own
  // layout via shallow merge — a section's explicitly set fields always win.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const defaultSectionLayout = (config as any)?.defaultSectionLayout as Record<string, unknown> | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const stickyContainer = (config as any)?.stickyContainer as StickyContainerConfig | undefined;

  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);
  useStickyScrollClearance(containerRef, contentRef, stickyRef,
    scrollable && Boolean(stickyContainer?.sections?.length), stickyContainer?.anchor === 'top' ? 'top' : 'bottom',
    (stickyContainer?.anchor === 'top' ? stickyContainer.offset?.top : stickyContainer?.offset?.bottom) ?? 0);
  const semanticMarkup = useContext(SemanticMarkupContext);
  const article = useContext(ArticleRenderContext);
  // G2 (PR7): with a palette page colour the ground is var(--ap-color-page) on the engine; a solid
  // canvas colour is the light-only legacy ground and would paint over the dark palette.
  const canvasBackdrop = article?.presentation?.palette?.light?.page && content?.canvas?.background?.kind === 'solid'
    ? undefined
    : content?.canvas?.background;
  // G2 (PR6, R8): token gutters — left/right padding from --ap-dim-layout-gutter (phone value in the first paint).
  const tokenGutter = Boolean(article?.presentation?.dimensions && hasOwn(article.presentation.dimensions, 'layout-gutter'));
  const contentInsetStyle = useMemo(() => {
    const insetStyle = mapEdgeInsetsToCSS(config?.insets);
    if (!tokenGutter) return insetStyle;
    const { paddingLeft: _left, paddingRight: _right, ...vertical } = insetStyle;
    return vertical;
  }, [config?.insets, tokenGutter]);

  const ep = editMode || previewMode === 'live' ? 'content' : undefined;

  // Sort sections by ascending order; skip Background (rendered as container layer).
  // Header and Footer render inline via SectionRenderer at their order position.
  const sortedSections = useMemo(
    () => [...sections]
      .filter((s) => s.kind !== 'Background')
      .sort((a, b) => a.order - b.order),
    [sections],
  );
  // Articles only: a real h1/h2/h3/p outline for zero-JS crawlers (SemanticMarkupContext); heading ids
  // from the document's defaultLanguage only in template articles (G2, D5; Q3 option B: a legacy article —
  // no presentation — keeps today's id-free headings, so ids in its CustomEmbed HTML can never collide).
  const headingIdLanguage = article?.presentation ? article.defaultLanguage : undefined;
  const semanticTagsBySection = useMemo(
    () => (semanticMarkup === 'article'
      ? resolveArticleSemanticTags(sortedSections, locale, { defaultLanguage: headingIdLanguage })
      : null),
    [sortedSections, locale, semanticMarkup, headingIdLanguage],
  );
  // G2 (D2): this screen's outline for the ArticleToc block; null context stays null.
  // G5 (R4): template articles also get the lead figure (first Image Media section) — eager + fetchpriority high.
  const articleWithOutline = useMemo(
    () => (article && semanticTagsBySection
      ? {
        ...article,
        outline: buildArticleOutline(sortedSections, semanticTagsBySection, locale),
        ...(article.presentation ? { leadFigureSectionId: findLeadFigureSectionId(sortedSections) } : {}),
      }
      : article),
    [article, semanticTagsBySection, sortedSections, locale],
  );
  // G2 Task 17 fix round 1 (M-1): template articles collapse adjacent spacing tokens like block margins
  // (gap = max(previous spaceAfter, own spaceBefore)); null outside template articles — funnels and legacy
  // articles keep each wrapper's own margins.
  const articleSpacing = article?.presentation?.spacing;
  const collapsedSpacing = useMemo(
    () => (articleSpacing
      ? collapseArticleSpacing(
        sortedSections
          .filter((section) => !sectionButtonStates?.[section.id]?.hidden)
          .map((section) => ({ id: section.id, kind: section.kind, layout: mergeSectionLayout(defaultSectionLayout, section.layout, scrollable) })),
        articleSpacing,
      )
      : null),
    [articleSpacing, sortedSections, sectionButtonStates, defaultSectionLayout, scrollable],
  );
  const allLinkableSections = stickyContainer?.sections
    ? [...sections, ...stickyContainer.sections]
    : sections;

  // If a Header section exists in the sections list, it renders inline via SectionRenderer.
  // Only render the standalone HeaderBar when no Header section exists.
  const hasHeaderSection = sections.some((s) => s.kind === 'Header');
  // If a Footer section exists, it renders inline via SectionRenderer as ActionBar.
  // Only render the standalone ActionBar when no Footer section exists.
  const hasFooterSection = sections.some((s) => s.kind === 'Footer');


  return (
    <div
      className={`${styles.container}${previewMode === 'live' ? ` ${styles.liveMode}` : ''}${editMode ? ' screen-view--edit-mode' : ''}`}
      ref={containerRef}
      data-testid="view-flexible-layout"
    >
      {/* Background layer */}
      <Background
        backdrop={canvasBackdrop}
        locale={locale}
        editPath={ep ? `${ep}.canvas.background` : undefined}
        previewMode={previewMode}
      />

      {/* Header navigation bar — only render here if no Header section exists in sections list */}
      {content?.header && !hasHeaderSection && (
        <HeaderBar
          header={content.header}
          locale={locale}
          variables={variables}
          step={step}
          onAction={onAction}
          editPath={ep ? `${ep}.header` : undefined}
        />
      )}

      {/* Sections container — apply container-level insets as padding */}
      <div
        ref={contentRef}
        className={scrollable ? styles.scrollable : styles.fixed}
        style={contentInsetStyle}
        data-article-content={tokenGutter ? '' : undefined}
      >
        <ArticleRenderContext.Provider value={articleWithOutline}>
        {sortedSections.map((section) => {
          const sectionEditPath = ep ? `${ep}.flexibleLayout.sections[${section.id}]` : undefined;

          // Per-section selection: extract this section's selected indices from the map
          const sectionSelectedIndices = selectedOptionIndicesMap?.get(section.id);

          // Per-section product selection: extract this section's selected product index
          const sectionProductIndex = selectedProductIndexMap?.get(section.id) ?? null;

          // Per-section button visibility: check if this button section should be disabled/hidden
          const buttonState = sectionButtonStates?.[section.id];
          if (buttonState?.hidden) return null; // hiddenUntilValueEntered: don't render
          const linkedProductRender = buildLinkedProductRenderResult({
            section,
            sections: allLinkableSections,
            selectedProductIndexMap,
            itemVariablesMap,
            variables,
            locale,
          });

          const sectionRendererProps: SectionRendererProps = {
            section: linkedProductRender.section,
            purchaseProductId: linkedProductRender.purchaseProductId,
            step,
            ssrMode,
            locale,
            variables: linkedProductRender.variables,
            resolvedScreenValues,
            onAction,
            onEvent,
            previewMode,
            editPath: sectionEditPath,
            selectedProductIndex: sectionProductIndex,
            onProductSelect: onSectionProductSelect
              ? (item, index) => onSectionProductSelect(section.id, item, index)
              : undefined,
            itemVariablesMap,
            selectedOptionIndices: sectionSelectedIndices,
            onOptionSelect: onSectionOptionSelect
              ? (item, index, selected) => onSectionOptionSelect(section.id, item, index, selected)
              : undefined,
            embedHtmlMap,
            embedInitDataMap,
            onEmbedNavigate: onSectionEmbedNavigate
              ? (data: Record<string, unknown>) => onSectionEmbedNavigate(section.id, data)
              : undefined,
            onEmbedBack: onSectionEmbedBack,
            onEmbedTrackEvent: onSectionEmbedTrackEvent,
            onEmbedValueChange: onSectionEmbedValueChange,
            onEmbedSetVariable: onSectionEmbedSetVariable,
            onEmbedTriggerOptionAction: onSectionEmbedTriggerOptionAction
              ? (request: CustomScreenOptionActionRequest) =>
                  onSectionEmbedTriggerOptionAction(section.id, request)
              : undefined,
            onEmbedTriggerButtonAction: onSectionEmbedTriggerButtonAction
              ? (request: CustomScreenButtonActionRequest) =>
                  onSectionEmbedTriggerButtonAction(section.id, request)
              : undefined,
            lastVariableUpdate,
            onEmbedSelectProduct: onSectionEmbedSelectProduct
              ? (childSectionId: string, productIndex: number, productId: string) =>
                  onSectionEmbedSelectProduct(section.id, childSectionId, productIndex, productId)
              : undefined,
            onEmbedPurchaseProduct: onSectionEmbedPurchaseProduct
              ? (requestId: string, productId: string, childSectionId?: string) =>
                  onSectionEmbedPurchaseProduct(section.id, requestId, productId, childSectionId)
              : undefined,
            embedPurchaseResult: sectionEmbedPurchaseResult,
            buttonDisabled: buttonState?.disabled,
            isLoading,
            textFieldValue: sectionTextFieldValues?.get(section.id),
            textFieldError: sectionTextFieldErrors?.get(section.id),
            onTextFieldChange: onSectionTextFieldChange
              ? (value: string) => onSectionTextFieldChange(section.id, value)
              : undefined,
            onTextFieldBlur: onSectionTextFieldBlur
              ? () => onSectionTextFieldBlur(section.id)
              : undefined,
            semanticTags: semanticTagsBySection?.get(section.id),
          };

          // Merge screen-level defaults under the section's own layout;
          // in scrollable mode flexGrow/flexShrink are zeroed after the merge
          // (flex sizing is meaningless in a scroll column).
          const effectiveLayout = mergeSectionLayout(defaultSectionLayout, section.layout, scrollable);

          return (
            <SectionWrapper
              key={section.id}
              layout={effectiveLayout}
              locale={locale}
              data-section-id={section.id}
              articleSpacing={collapsedSpacing?.get(section.id)}
            >
              <SectionRenderer {...sectionRendererProps} />
            </SectionWrapper>
          );
        })}
        </ArticleRenderContext.Provider>
      </div>

      {/* Sticky floating container for CTA buttons */}
      {stickyContainer && (
        <StickyContainer
          containerRef={stickyRef}
          stickyContainer={stickyContainer}
          step={step}
          locale={locale}
          variables={variables}
          onAction={onAction}
          onEvent={onEvent}
          previewMode={previewMode}
          editMode={editMode}
          sectionButtonStates={sectionButtonStates}
          isLoading={isLoading}
          allSections={allLinkableSections}
          selectedProductIndexMap={selectedProductIndexMap}
          itemVariablesMap={itemVariablesMap}
          editPath={ep ? `${ep}.flexibleLayout` : undefined}
        />
      )}

      {/* Action bar (bottom navigation / CTA) — only render standalone when no Footer section exists */}
      {content?.actionBar && !hasFooterSection && (
        <ActionBar
          actionBar={content.actionBar}
          locale={locale}
          variables={variables}
          step={step}
          onAction={onAction}
          editPath={ep ? `${ep}.actionBar` : undefined}
        />
      )}
    </div>
  );
});

FlexibleLayout.displayName = 'FlexibleLayout';
